import { api } from '@/api';
import type { Page } from '@/platform/types';
import type {
  AccessGroup,
  Area,
  AreaRef,
  PayoutRow,
  QuestionReviewRef,
  Review,
  Reviewer,
  ReviewerProfileInput,
  ReviewFilter,
  ReviewQueue,
  ReviewVerdict,
  AccessStudent,
  AnswerResponse,
  Attempt,
  Category,
  FeedbackMode,
  MyQuiz,
  OverviewReport,
  PendingAnswer,
  Question,
  QuestionInput,
  QuizDetail,
  QuizInput,
  QuizReport,
  QuizSummary,
  Result,
  ResultInsights,
  SavedAnswer,
  StaffDashboard,
  StudentQuestion,
  StudentReport,
} from './types';

const BASE = '/assessments';

// ── Categories ──────────────────────────────────────────────────────────────
export const listCategories = (signal?: AbortSignal) =>
  api.get<Category[]>(`${BASE}/categories`, { signal });

export interface CategoryInput {
  name: string;
  parentId: string | null;
  color: string | null;
  description: string | null;
}
export const createCategory = (input: CategoryInput) =>
  api.post<Category>(`${BASE}/categories`, input);
export const updateCategory = (id: string, input: CategoryInput) =>
  api.patch<Category>(`${BASE}/categories/${id}`, input);
export const deleteCategory = (id: string) => api.delete<void>(`${BASE}/categories/${id}`);

// ── Questions ───────────────────────────────────────────────────────────────
export interface QuestionQuery {
  search?: string;
  categoryId?: string;
  type?: string;
  difficulty?: string;
  status?: string;
  excludeQuizId?: string;
  page?: number;
  pageSize?: number;
}

export const listQuestions = (query: QuestionQuery = {}, signal?: AbortSignal) =>
  api.get<Page<Question>>(`${BASE}/questions`, { query: { ...query }, signal });
export const getQuestion = (id: string, signal?: AbortSignal) =>
  api.get<Question>(`${BASE}/questions/${id}`, { signal });
export const createQuestion = (input: QuestionInput) =>
  api.post<Question>(`${BASE}/questions`, input);
export const updateQuestion = (id: string, input: QuestionInput) =>
  api.put<Question>(`${BASE}/questions/${id}`, input);
export const duplicateQuestion = (id: string) =>
  api.post<Question>(`${BASE}/questions/${id}/duplicate`);
export const deleteQuestion = (id: string) => api.delete<void>(`${BASE}/questions/${id}`);
export const questionStats = (signal?: AbortSignal) =>
  api.get<{ total: number; byType: Record<string, number>; byDifficulty: Record<string, number> }>(
    `${BASE}/questions/stats`,
    { signal },
  );

// ── Quizzes ─────────────────────────────────────────────────────────────────
export const listQuizzes = (courseId?: string, signal?: AbortSignal) =>
  api.get<QuizSummary[]>(`${BASE}/quizzes`, { query: { courseId }, signal });
export const getQuiz = (id: string, signal?: AbortSignal) =>
  api.get<QuizDetail>(`${BASE}/quizzes/${id}`, { signal });
export const createQuiz = (input: QuizInput) => api.post<QuizDetail>(`${BASE}/quizzes`, input);
export const updateQuiz = (id: string, input: Partial<QuizInput> & { status?: string }) =>
  api.patch<QuizDetail>(`${BASE}/quizzes/${id}`, input);
export const deleteQuiz = (id: string) => api.delete<void>(`${BASE}/quizzes/${id}`);
export const duplicateQuiz = (id: string) =>
  api.post<QuizDetail>(`${BASE}/quizzes/${id}/duplicate`);
export const setQuizItems = (id: string, items: { questionId: string; points?: number }[]) =>
  api.put<QuizDetail>(`${BASE}/quizzes/${id}/items`, { items });
/** Students of the institution in the top bar, ticked when the quiz is released to them. */
export const getQuizAccess = (id: string, courseId?: string, signal?: AbortSignal) =>
  api.get<{ students: AccessStudent[]; groups: AccessGroup[]; released: number }>(
    `${BASE}/quizzes/${id}/access`,
    {
      query: { courseId },
      signal,
    },
  );
/** The version a release hands out: every question (null) or the chosen ones, and the feedback. */
export interface ReleaseVersion {
  questionIds: string[] | null;
  feedbackMode: FeedbackMode;
}
/** Releases (or takes back) the quiz for these students. */
export const setQuizAccess = (
  id: string,
  userIds: string[],
  released: boolean,
  version?: ReleaseVersion,
) =>
  api.put<{ changed: number }>(`${BASE}/quizzes/${id}/access`, { userIds, released, ...version });
/** Releases (or takes back) the quiz for whole turmas, including students who join later. */
export const setGroupAccess = (
  id: string,
  groupIds: string[],
  released: boolean,
  version?: ReleaseVersion,
) =>
  api.put<{ changed: number }>(`${BASE}/quizzes/${id}/access/groups`, {
    groupIds,
    released,
    ...version,
  });
export interface GenerateRule {
  categoryId: string | null;
  difficulty: string | null;
  type: string | null;
  count: number;
}
export const generateQuizItems = (id: string, rules: GenerateRule[], replace: boolean) =>
  api.post<{ added: number; shortages: string[] }>(`${BASE}/quizzes/${id}/generate`, {
    rules,
    replace,
  });
export const previewQuiz = (id: string, signal?: AbortSignal) =>
  api.get<StudentQuestion[]>(`${BASE}/quizzes/${id}/preview`, { signal });
export const regradeQuiz = (id: string) =>
  api.post<{ regraded: number }>(`${BASE}/quizzes/${id}/regrade`);

// ── Student ─────────────────────────────────────────────────────────────────
export const myQuizzes = (signal?: AbortSignal) =>
  api.get<MyQuiz[]>(`${BASE}/my/quizzes`, { signal });
export const myQuiz = (id: string, signal?: AbortSignal) =>
  api.get<MyQuiz>(`${BASE}/my/quizzes/${id}`, { signal });
export const myPerformance = (signal?: AbortSignal) =>
  api.get<StudentReport>(`${BASE}/my/performance`, { signal });
export const startAttempt = (quizId: string) =>
  api.post<Attempt>(`${BASE}/my/quizzes/${quizId}/attempts`);
export const getAttempt = (id: string, signal?: AbortSignal) =>
  api.get<Attempt>(`${BASE}/attempts/${id}`, { signal });
export const saveAnswer = (
  attemptId: string,
  questionId: string,
  body: { response?: AnswerResponse; flagged?: boolean },
) => api.put<SavedAnswer>(`${BASE}/attempts/${attemptId}/answers/${questionId}`, body);
/** Immediate-feedback quizzes: confirms the answer and returns the answer key. */
export const checkAnswer = (attemptId: string, questionId: string, response: AnswerResponse) =>
  api.post<SavedAnswer>(`${BASE}/attempts/${attemptId}/answers/${questionId}/check`, {
    response,
  });
export const reportFocusLost = (attemptId: string) =>
  api.post<void>(`${BASE}/attempts/${attemptId}/events`, { type: 'focus_lost' });
// Submit and result come in a burst when a whole class hits the deadline: wait in line longer.
export const submitAttempt = (id: string) =>
  api.post<void>(`${BASE}/attempts/${id}/submit`, undefined, { timeoutMs: 60_000 });
export const getResult = (id: string, signal?: AbortSignal) =>
  api.get<Result>(`${BASE}/attempts/${id}/result`, { signal, timeoutMs: 60_000 });
export const getInsights = (id: string, signal?: AbortSignal) =>
  api.get<ResultInsights>(`${BASE}/attempts/${id}/insights`, { signal, timeoutMs: 60_000 });

// ── Staff: attempts, grading ────────────────────────────────────────────────
export const reviewAttempt = (id: string, signal?: AbortSignal) =>
  api.get<Result>(`${BASE}/attempts/${id}/review`, { signal });
export const reopenAttempt = (id: string, minutes: number) =>
  api.post<Result>(`${BASE}/attempts/${id}/reopen`, undefined, { query: { minutes } });
export const pendingAnswers = (quizId?: string, signal?: AbortSignal) =>
  api.get<PendingAnswer[]>(`${BASE}/grading/pending`, { query: { quizId }, signal });
export const gradeAnswer = (answerId: string, score: number, feedback: string | null) =>
  api.patch<Result>(`${BASE}/answers/${answerId}`, { score, feedback });

// ── Reports ─────────────────────────────────────────────────────────────────
export const staffDashboard = (signal?: AbortSignal) =>
  api.get<StaffDashboard>(`${BASE}/dashboard`, { signal });
export const overviewReport = (courseId?: string, signal?: AbortSignal) =>
  api.get<OverviewReport>(`${BASE}/reports/overview`, { query: { courseId }, signal });
export const quizReport = (id: string, signal?: AbortSignal) =>
  api.get<QuizReport>(`${BASE}/reports/quizzes/${id}`, { signal });
export const studentReport = (id: string, courseId?: string, signal?: AbortSignal) =>
  api.get<StudentReport>(`${BASE}/reports/students/${id}`, { query: { courseId }, signal });

export const PATHS = {
  resultsCsv: (id: string) => `${BASE}/reports/quizzes/${id}/results.csv`,
  itemsCsv: (id: string) => `${BASE}/reports/quizzes/${id}/items.csv`,
  questionsImport: `${BASE}/questions/import`,
  questionsTemplate: `${BASE}/questions/import/template`,
  categoriesImport: `${BASE}/categories/import`,
  categoriesTemplate: `${BASE}/categories/import/template`,
};

// ── Question review ─────────────────────────────────────────────────────────
const REVIEW = `${BASE}/review`;

export const listReviewAreas = (signal?: AbortSignal) =>
  api.get<Area[]>(`${REVIEW}/areas`, { signal });
export const listReviewers = (signal?: AbortSignal) =>
  api.get<Reviewer[]>(`${REVIEW}/reviewers`, { signal });
export const inviteReviewer = (input: {
  email: string;
  name: string;
  areaIds: string[];
  rateCents: number;
}) => api.post<Reviewer>(`${REVIEW}/reviewers`, input);
export const updateReviewer = (
  userId: string,
  input: Partial<{
    areaIds: string[];
    rateCents: number;
    status: 'active' | 'suspended';
    note: string | null;
  }>,
) => api.patch<Reviewer>(`${REVIEW}/reviewers/${userId}`, input);
export const listPayouts = (month: string, signal?: AbortSignal) =>
  api.get<PayoutRow[]>(`${REVIEW}/payouts`, { query: { month }, signal });
export const markPaid = (userId: string, month: string) =>
  api.post<PayoutRow[]>(`${REVIEW}/payouts/${userId}/paid`, undefined, { query: { month } });
export const payoutsCsvPath = (month: string) => `${REVIEW}/payouts.csv?month=${month}`;

export const getMyReviewer = (signal?: AbortSignal) =>
  api.get<Reviewer>(`${REVIEW}/me`, { signal });
export const submitReviewerProfile = (input: ReviewerProfileInput) =>
  api.put<Reviewer>(`${REVIEW}/me`, input);
export const getReviewQueue = (
  params: { status: ReviewFilter; areaId?: string; search?: string; page?: number },
  signal?: AbortSignal,
) => api.get<ReviewQueue>(`${REVIEW}/questions`, { query: params, signal });
export const getReviewQuestion = (id: string, signal?: AbortSignal) =>
  api.get<{ question: Question; area: AreaRef | null; myReview: Review | null }>(
    `${REVIEW}/questions/${id}`,
    { signal },
  );
export const saveReview = (id: string, input: { verdict: ReviewVerdict; comment: string | null }) =>
  api.put<Review>(`${REVIEW}/questions/${id}`, input);
export const getQuestionReviews = (id: string, signal?: AbortSignal) =>
  api.get<QuestionReviewRef[]>(`${REVIEW}/questions/${id}/reviews`, { signal });
